import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { FileDown, Send, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  draft: "bg-anvi-100 text-anvi-700",
  sent: "bg-blue-50 text-blue-700",
  viewed: "bg-purple-50 text-purple-800",
  approved: "bg-emerald-50 text-emerald-700",
};

export default function Quotations() {
  const [quotations, setQuotations] = useState([]);

  const load = () => api.quotationsAll().then((r) => setQuotations(r.data));
  useEffect(() => { load(); }, []);

  const openPdf = (id) => window.open(api.quotationPdfUrl(id), "_blank");
  const sendQuo = async (id) => {
    try { await api.quotationMarkSent(id); toast.success("Marked as sent (WhatsApp/Email MOCKED)"); load(); }
    catch { toast.error("Failed"); }
  };

  const totalValue = quotations.reduce((s, q) => s + (q.grand_total || 0), 0);
  const sentCount = quotations.filter((q) => q.status !== "draft").length;

  return (
    <div className="px-10 pt-4 pb-10 animate-fade-in" data-testid="quotations-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-2">Proposals</div>
          <h1 className="font-heading text-4xl text-anvi-900">Quotations</h1>
          <p className="text-sm text-anvi-500 mt-2">{quotations.length} quotations across all projects · {sentCount} sent · {inr(totalValue)} proposed value</p>
        </div>
        <div className="text-xs text-anvi-500">
          To generate a new quotation, open a project → <span className="font-medium text-anvi-terracotta">Quotations tab</span>
        </div>
      </div>

      <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200 bg-anvi-50/50">
              <th className="px-4 py-3 text-left font-medium">Quote #</th>
              <th className="px-4 py-3 text-left font-medium">Version</th>
              <th className="px-4 py-3 text-left font-medium">Project</th>
              <th className="px-4 py-3 text-left font-medium">Client</th>
              <th className="px-4 py-3 text-left font-medium">Tier</th>
              <th className="px-4 py-3 text-right font-medium">Grand Total</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Created</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {quotations.map((q) => (
              <tr key={q.id} className="border-b border-anvi-100 hover:bg-anvi-50/60 transition-colors" data-testid={`global-quotation-${q.id}`}>
                <td className="px-4 py-3.5 font-mono text-xs text-anvi-700">{q.quote_number}</td>
                <td className="px-4 py-3.5 font-heading text-lg text-anvi-900">v{q.version}</td>
                <td className="px-4 py-3.5">
                  <Link to={`/projects/${q.project_id}`} className="text-sm text-anvi-900 hover:text-anvi-terracotta flex items-center gap-1">
                    {q.project_name} <ArrowUpRight size={11} className="text-anvi-400" />
                  </Link>
                </td>
                <td className="px-4 py-3.5 text-sm text-anvi-500">{q.client_name}</td>
                <td className="px-4 py-3.5 text-sm text-anvi-900">{q.tier}</td>
                <td className="px-4 py-3.5 text-sm text-right font-medium tabular-nums text-anvi-900">{inr(q.grand_total)}</td>
                <td className="px-4 py-3.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_TONE[q.status]}`}>{q.status}</span>
                </td>
                <td className="px-4 py-3.5 text-xs text-anvi-500 tabular-nums">{new Date(q.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3.5 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button onClick={() => openPdf(q.id)} data-testid={`global-open-pdf-${q.id}`}
                            className="text-anvi-terracotta hover:bg-anvi-terracotta/10 p-1.5 rounded-md" title="Download PDF">
                      <FileDown size={14} strokeWidth={1.5} />
                    </button>
                    {q.status === "draft" ? (
                      <button onClick={() => sendQuo(q.id)} data-testid={`global-send-${q.id}`}
                              className="text-xs bg-anvi-900 hover:bg-anvi-800 text-white px-2.5 py-1 rounded-md flex items-center gap-1">
                        <Send size={11} strokeWidth={1.5} /> Send
                      </button>
                    ) : (
                      <span className="text-xs text-emerald-700 flex items-center gap-1"><Check size={11} strokeWidth={2} /> sent</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {quotations.length === 0 && (
              <tr><td colSpan={9} className="text-center py-16 text-sm text-anvi-500">No quotations yet · open a project and generate one from the Quotations tab</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
